import { useEffect, useRef, useState } from 'react';

// Reusable hook to capture fast keyboard input from HID RFID/card readers.
// The buffer completes on the pause after the last character, so a reader
// without a trailing Enter and a card id pasted with Strg+V behave the same.
export default function useCardScanner({ enabled = true, onComplete, debounceMs = 100 }) {
  const [inputValue, setInputValue] = useState('');
  const [buffer, setBuffer] = useState('');
  const timeoutRef = useRef(null);
  // The completion callback is re-created on every render of the consumer; the
  // debounce timer must call the current one, not the one captured when the
  // first character arrived.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!enabled) return;

    const ensureFocus = () => {
      const el = document.getElementById('rfid-input');
      if (el) el.focus();
      return el;
    };

    let el = ensureFocus();
    if (!el) setTimeout(ensureFocus, 100);

    const handleFocusLoss = () => ensureFocus();

    const handleGlobalKeyDown = e => {
      const current = document.getElementById('rfid-input');
      if (!current || e.target?.id === 'rfid-input') return;
      // Pull the focus back so the rest of the scan - and a Strg+V paste, whose
      // paste event follows the keydown - lands in the buffer input.
      current.focus();
      // Only printable characters belong in the buffer. Modifier combos
      // (Strg+V) and control keys ('Enter', 'Shift', 'Tab') would otherwise be
      // appended by name and corrupt the scanned card id.
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
      current.value = current.value + e.key;
      current.dispatchEvent(new Event('input', { bubbles: true }));
    };

    document.addEventListener('click', handleFocusLoss);
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => {
      document.removeEventListener('click', handleFocusLoss);
      document.removeEventListener('keydown', handleGlobalKeyDown);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !inputValue) return;
    const newBuffer = buffer + inputValue;
    setBuffer(newBuffer);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      onCompleteRef.current && onCompleteRef.current(newBuffer);
      setBuffer('');
    }, debounceMs);
    setInputValue('');
  }, [inputValue]);

  const ScannerInput = enabled ? (
    <input
      id='rfid-input'
      type='text'
      value={inputValue}
      onChange={e => setInputValue(e.target.value)}
      onBlur={e => e.target.focus()}
      autoFocus
      autoComplete='off'
      spellCheck='false'
      style={{
        position: 'absolute',
        left: -9999,
        opacity: 0,
        pointerEvents: 'none',
        width: 1,
        height: 1,
      }}
    />
  ) : null;

  return { ScannerInput };
}
